import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { Actions as ActionsLayout } from '@sorare/core/src/atoms/layout/Actions';
import { NotAndroidAppFeature } from '@sorare/core/src/components/BuyableFeature';
import { cart, glossary } from '@sorare/core/src/lib/glossary';
import {
  ClickAddToCart_Origin,
  ClickInstantBuy_Flow,
} from '@sorare/core/src/protos/events/so5/shared/events';

import { AuctionsActions } from '#src/components/auction/AuctionsActions/index.tsx';
import { BuyField } from '#src/components/buyActions/BuyField/index.tsx';
import { AddToCart } from '#src/components/cart/AddToCart/index.tsx';
import { MakeOffer } from '#src/components/directOffer/MakeOffer/index.tsx';
import { PrimaryBuyBuyField } from '#src/components/primaryBuy/PrimaryBuyBuyField/index.tsx';
import { useGetAuctionDetails } from '#src/hooks/offers/useGetAuctionDetails.ts';
import { useGetTokenSingleSaleDetails } from '#src/hooks/offers/useGetTokenSingleSaleDetails.ts';
import { useIsPrimaryBuyLive } from '#src/hooks/primaryBuy/useIsPrimaryBuyLive.ts';

import type { PlayerDrawerTokenActions_anyCard } from './__generated__/index.graphql.ts';

type Origin = {
  instantBuy: ClickInstantBuy_Flow;
  addToCart: ClickAddToCart_Origin;
};

type Props = {
  anyCard: PlayerDrawerTokenActions_anyCard;
  origin?: Origin;
};

export const Actions = ({
  anyCard,
  origin = {
    instantBuy: ClickInstantBuy_Flow.SCOUTING_CENTER,
    addToCart: ClickAddToCart_Origin.SCOUTING_CENTER,
  },
}: Props) => {
  const auction = useGetAuctionDetails(anyCard.latestEnglishAuction);
  const offer = useGetTokenSingleSaleDetails(anyCard);
  const primaryBuy = useIsPrimaryBuyLive(anyCard);

  const actions = (() => {
    if (primaryBuy) {
      return (
        <ActionsLayout direction="horizontal" gap={1}>
          <AddToCart
            anyCard={anyCard}
            size="small"
            color="tertiary"
            origin={origin.addToCart}
            fullWidth
          >
            <FormattedMessage {...cart.addToCart} />
          </AddToCart>
          <PrimaryBuyBuyField
            card={anyCard}
            size="small"
            color="quaternary"
            origin={origin.instantBuy}
            primaryBuyConfirmationOptions={{
              actions: {
                primary: {
                  label: <FormattedMessage {...glossary.gotIt} />,
                },
              },
            }}
            fullWidth
          />
        </ActionsLayout>
      );
    }
    if (offer)
      return (
        <ActionsLayout direction="horizontal" gap={1}>
          <NotAndroidAppFeature>
            <MakeOffer size="small" color="quaternary" card={anyCard} />
          </NotAndroidAppFeature>
          <BuyField
            fullWidth
            card={anyCard}
            variant="small"
            color="quaternary"
          />
        </ActionsLayout>
      );
    if (auction && anyCard.latestEnglishAuction)
      return <AuctionsActions auction={anyCard.latestEnglishAuction} />;

    return (
      <Button size="small" fullWidth color="quaternary" disabled>
        <FormattedMessage {...glossary.transferInProgress} />
      </Button>
    );
  })();

  return actions;
};

Actions.fragments = {
  anyCard: gql`
    fragment PlayerDrawerTokenActions_anyCard on AnyCardInterface {
      slug
      latestEnglishAuction {
        id
        ...useGetAuctionDetails_auction
        ...AuctionsActions_auction
      }
      ...useGetTokenSingleSaleDetails_anyCard
      ...useIsPrimaryBuyLive_anyCard
      ...PrimaryBuyBuyField_anyCard
      ...BuyField_anyCard
      ...MakeOffer_anyCard
      ...AddToCart_anyCard
    }
    ${useGetAuctionDetails.fragments.auction}
    ${useGetTokenSingleSaleDetails.fragments.anyCard}
    ${useIsPrimaryBuyLive.fragments.anyCard}
    ${PrimaryBuyBuyField.fragments.anyCard}
    ${BuyField.fragments.anyCard}
    ${MakeOffer.fragments.anyCard}
    ${AuctionsActions.fragments.auction}
    ${AddToCart.fragments.anyCard}
  ` as TypedDocumentNode<PlayerDrawerTokenActions_anyCard>,
};
